import { ClassData } from "../../../common/dtos/classes.type";

/**
 * Parse an ISO 8601 duration (e.g. "P05Y06M") into total months.
 */
function durationToMonths(duration: string): number {
  const match = duration.match(/P(\d+)Y(\d+)M/);
  if (!match) return 0;
  return parseInt(match[1], 10) * 12 + parseInt(match[2], 10);
}

/**
 * Filter camps by age range from URL params.
 * Supports both ISO 8601 range format ("P05Y00M-P06Y00M")
 * and simple year number ("5").
 */
export function filterCampsByAge(
  camps: ClassData[],
  ageRange: string
): ClassData[] {
  if (!ageRange || ageRange === "null") return camps;

  // Simple year number (e.g. "5")
  const yearMatch = ageRange.match(/^\d+$/);
  if (yearMatch) {
    const ageMonths = parseInt(ageRange, 10) * 12;
    return camps.filter((camp) => {
      const campMinMonths = durationToMonths(camp.min_age);
      const campMaxMonths = durationToMonths(camp.max_age);
      // Check if the age falls within the camp's age range
      return campMinMonths <= ageMonths && campMaxMonths >= ageMonths;
    });
  }

  // ISO 8601 range format
  const parts = ageRange.split("-");
  if (parts.length < 2) return camps;

  // Rejoin in case of "P05Y00M-P06Y00M" format
  const minStr = parts[0];
  const maxStr = parts.slice(1).join("-");
  const filterMinMonths = durationToMonths(minStr);
  const filterMaxMonths = durationToMonths(maxStr);

  return camps.filter((camp) => {
    const campMinMonths = durationToMonths(camp.min_age);
    const campMaxMonths = durationToMonths(camp.max_age);

    // Check for overlap between filter range and camp age range
    return campMinMonths <= filterMaxMonths && campMaxMonths >= filterMinMonths;
  });
}

/**
 * Filter camps by gender from URL params.
 * "All" matches everything, "Male"/"Female" for specific.
 */
export function filterCampsByGender(
  camps: ClassData[],
  gender: string
): ClassData[] {
  if (!gender || gender === "null") return camps;

  const normalizedGender = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();

  return camps.filter((camp) => {
    return camp.gender === "All" || camp.gender === normalizedGender;
  });
}

/**
 * Filter camps by session name.
 */
export function filterCampsBySession(
  camps: ClassData[],
  session: string
): ClassData[] {
  if (!session) return camps;

  return camps.filter((camp: any) => {
    return camp.session === session;
  });
}

/**
 * Filter camps by month (format: "june-2026" or "july-2026").
 * Matches against camp start_date.
 */
export function filterCampsByMonth(
  camps: ClassData[],
  monthSlug: string
): ClassData[] {
  if (!monthSlug) return camps;

  const MONTHS = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
  ];

  // Parse "june-2026" into month index + year
  const parts = monthSlug.toLowerCase().split("-");
  if (parts.length < 2) return camps;

  const monthName = parts[0];
  const year = parseInt(parts[1], 10);
  const monthIndex = MONTHS.indexOf(monthName);
  if (monthIndex === -1 || isNaN(year)) return camps;

  return camps.filter((camp: any) => {
    if (!camp.start_date) return false;
    const dateParts = camp.start_date.split("-");
    if (dateParts.length < 2) return false;
    const campYear = parseInt(dateParts[0], 10);
    const campMonth = parseInt(dateParts[1], 10) - 1; // 0-indexed
    return campYear === year && campMonth === monthIndex;
  });
}
